import { useQuery } from '@tanstack/react-query';
import { type AccountAddress, type AccountContact, accountApi } from '@/api/commercial/accounts';
import type { CreditAnalysis } from '@/api/commercial/credit-analysis';
import type { Proposal, ProposalPayment } from '@/api/commercial/proposals';
import { proposalApi } from '@/api/commercial/proposals';
import { useCartCalculate } from '@/hooks/commercial/use-cart-calculate';
import type { ICartCalculateResponse, IProposalCartItem } from '@/types/commercial/cart';
import {
  type ProposalCreditAnalysisResolution,
  useProposalCreditAnalysisResolution,
} from './use-proposal-credit-analysis-resolution';

export interface ResumeData {
  proposal: Proposal | null;
  payment: ProposalPayment | null;
  cartItems: IProposalCartItem[];
  calculateData: ICartCalculateResponse | null;
  contacts: AccountContact[];
  deliveryAddress: AccountAddress | null;
  creditAnalysis: CreditAnalysis | null;
  creditAnalysisResolution: ProposalCreditAnalysisResolution | null;
  isLoading: boolean;
}

export function useResumeData(proposalId?: string, clientId?: string): ResumeData {
  const { data: proposal, isLoading: isLoadingProposal } = useQuery({
    queryKey: ['proposal', proposalId],
    queryFn: () => proposalApi.findOne(proposalId!),
    enabled: !!proposalId,
  });

  const { data: calculateData, isLoading: isLoadingCalc } = useCartCalculate(proposalId);

  const addressesResult = useQuery({
    queryKey: ['addresses', clientId],
    queryFn: () => accountApi.findAddresses(clientId!),
    enabled: !!clientId,
  });
  const creditResolutionResult = useProposalCreditAnalysisResolution(proposalId);

  const contacts = proposal?.contact ? ([proposal.contact] as AccountContact[]) : [];
  const addresses = (addressesResult.data as AccountAddress[] | undefined) ?? [];
  const creditAnalysisResolution = creditResolutionResult.data ?? null;
  const creditAnalysis = creditAnalysisResolution?.analysis ?? null;

  const deliveryAddressId = proposal?.deliveryAddressId;
  const deliveryAddress: AccountAddress | null =
    proposal?.deliveryMode === 'BWS' && proposal.pickupAddressSnapshot
      ? {
          ...proposal.pickupAddressSnapshot,
          id: proposal.pickupLocationId!,
          accountId: clientId ?? '',
          complement: proposal.pickupAddressSnapshot.complement ?? undefined,
        }
      : deliveryAddressId
        ? (addresses.find((a) => a.id === deliveryAddressId) ?? null)
        : null;

  const isLoading =
    isLoadingProposal ||
    isLoadingCalc ||
    addressesResult.isLoading ||
    creditResolutionResult.isLoading;

  return {
    proposal: proposal ?? null,
    payment: proposal?.payment ?? null,
    cartItems: calculateData?.items ?? [],
    calculateData: calculateData ?? null,
    contacts,
    deliveryAddress: deliveryAddress ?? null,
    creditAnalysis,
    creditAnalysisResolution,
    isLoading,
  };
}
